// Carry Roll Roll model — PSD §4.2.4. Post-landing run-out (feature 1.2).
import { DT_ROLL, FIRMNESS_FACTOR, FRICTION_BASE, G, MAX_ROLL, MOISTURE_FACTOR, ROLL_STOP_V, } from './constants';
import { degToRad } from './units';
/** Effective friction coefficient from surface, firmness, moisture, slope. */
export function effectiveFriction(env) {
    let mu = FRICTION_BASE[env.landingSurface] ?? FRICTION_BASE.fairway;
    mu *= FIRMNESS_FACTOR[env.firmness] ?? 1;
    mu *= MOISTURE_FACTOR[env.moisture] ?? 1;
    // Slope factor: downhill (positive) increases roll (less friction), uphill decreases.
    const slope = Math.tan(degToRad(env.landingSlopeDeg));
    mu = mu - slope * 0.6;
    return Math.max(mu, 0.05);
}
/** Stop factor: steeper landing + more spin → shorter roll. */
export function stopFactor(landingAngleDeg, spinRpm) {
    const angleTerm = (landingAngleDeg - 30) / 50;
    const spinTerm = (spinRpm - 2000) / 9000;
    const f = 1 - clamp(angleTerm, 0, 0.6) - clamp(spinTerm, 0, 0.4);
    return clamp(f, 0.05, 1.0);
}
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
/**
 * Constant-deceleration ground model.
 * Landing-angle override (2.8) replaces the computed angle when non-null.
 */
export function simulateRoll(input) {
    const warnings = [];
    const { carryM, env, club, vxLand, zLand, override } = input;
    const useAngle = override.landingAngleDeg ?? input.landingAngleDeg;
    const v0 = Math.max(vxLand, 0);
    const mu = effectiveFriction(env);
    const sf = stopFactor(useAngle, input.spinRpm);
    let v = v0;
    let d = 0;
    let t = 0;
    const path = [];
    if (v0 > ROLL_STOP_V) {
        path.push({ t, x: carryM, y: 0, z: zLand, vx: v, vy: 0, vz: 0 });
        while (v > ROLL_STOP_V && d < MAX_ROLL) {
            const a = -mu * G;
            v = Math.max(0, v + a * DT_ROLL);
            d += v * DT_ROLL;
            t += DT_ROLL;
            // Cross-slope lateral drift during roll (5.8)
            const lateralDrift = Math.tan(degToRad(env.crossSlopeDeg)) * v * DT_ROLL;
            path.push({ t, x: carryM + d, y: 0, z: zLand + lateralDrift, vx: v, vy: 0, vz: 0 });
            if (path.length > 1000)
                break; // guard
        }
    }
    let roll = d * sf;
    if (club?.rollPercent != null) {
        roll = clamp(roll, 0, carryM * club.rollPercent * 1.5);
    }
    return {
        rollM: Math.max(roll, 0),
        rollPath: path,
        offlineM: path.length > 0 ? (path[path.length - 1].z ?? zLand) : zLand,
        warnings,
    };
}
//# sourceMappingURL=roll.js.map